import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { Check, Heart, Minus, Plus, AlertCircle } from "lucide-react";

import { API } from "@/lib/api";

const MENU_KEYS = ["lubina", "iberico", "menu_14_17", "menu_6_13", "menu_2_5"];

const emptyMenus = () => ({ lubina: 0, iberico: 0, menu_14_17: 0, menu_6_13: 0, menu_2_5: 0 });

const RSVPForm = () => {
  const { t, lang } = useLang();
  const [step, setStep] = useState("form"); // form | success
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "",
    attending: true,
    menus: emptyMenus(),
    total_attendees: 1,
    allergies: "",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const totalAttendees = Math.max(1, Number(form.total_attendees) || 1);
  const assigned = useMemo(
    () => MENU_KEYS.reduce((s, k) => s + (form.menus[k] || 0), 0),
    [form.menus]
  );
  const remaining = totalAttendees - assigned;
  const over = assigned > totalAttendees;
  const incomplete = form.attending && assigned !== totalAttendees;

  const setMenu = (key, delta) => {
    setForm((f) => {
      const current = f.menus[key] || 0;
      const next = current + delta;
      if (next < 0) return f;
      const newAssigned = MENU_KEYS.reduce(
        (s, k) => s + (k === key ? next : f.menus[k] || 0),
        0
      );
      if (delta > 0 && newAssigned > Math.max(1, Number(f.total_attendees) || 1)) {
        return f;
      }
      return { ...f, menus: { ...f.menus, [key]: next } };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(t.rsvp.errorRequired);
      return;
    }
    if (form.attending && incomplete) {
      toast.error(over ? t.rsvp.overAssigned : t.rsvp.assignAll);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/rsvp`, {
        name: form.name.trim(),
        attending: form.attending,
        menus: form.attending ? form.menus : emptyMenus(),
        total_attendees: totalAttendees,
        allergies: form.allergies,
        message: form.message,
        language: lang,
      });
      setStep("success");
    } catch (err) {
      toast.error(t.rsvp.errorGeneric);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm({
      name: "",
      attending: true,
      menus: emptyMenus(),
      total_attendees: 1,
      allergies: "",
      message: "",
    });
    setStep("form");
  };

  return (
    <section
      id="rsvp"
      data-testid="rsvp-section"
      className="py-28 md:py-40 px-6"
    >
      <div className="max-w-2xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-[11px] tracking-[0.4em] uppercase text-olive text-center mb-6"
        >
          {t.rsvp.label}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="font-serif text-4xl md:text-5xl text-wine text-center mb-4"
        >
          {t.rsvp.title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="font-editorial italic text-base md:text-lg text-olive text-center mb-12"
        >
          {t.rsvp.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="bg-white rounded-2xl p-8 md:p-12 shadow-[0_8px_30px_rgb(107,45,49,0.07)] border border-olive/10"
        >
          {step === "success" ? (
            <div className="text-center py-6" data-testid="rsvp-success">
              <div className="w-16 h-16 rounded-full bg-olive/15 mx-auto flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-wine" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-3xl text-wine mb-3">{t.rsvp.successTitle}</h3>
              <p className="font-editorial italic text-lg text-charcoal mb-8">
                {form.attending ? t.rsvp.successYes : t.rsvp.successNo}
              </p>
              <button
                onClick={reset}
                data-testid="rsvp-new-response-btn"
                className="text-[11px] tracking-[0.3em] uppercase text-olive border-b border-olive/40 hover:text-wine hover:border-wine transition-colors duration-300 pb-1"
              >
                {t.rsvp.newResponse}
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-7">
              <Field label={t.rsvp.name}>
                <input
                  data-testid="rsvp-name-input"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder={t.rsvp.namePh}
                  className="w-full bg-transparent border-b border-olive/25 px-1 py-3 font-sans text-base focus:outline-none focus:border-wine transition-colors placeholder:text-olive/50 placeholder:font-light"
                />
              </Field>

              <Field label={t.rsvp.attending}>
                <div className="flex gap-3 pt-1">
                  <ChoiceBtn
                    active={form.attending === true}
                    onClick={() => update("attending", true)}
                    testid="rsvp-attending-yes"
                  >
                    {t.rsvp.yes}
                  </ChoiceBtn>
                  <ChoiceBtn
                    active={form.attending === false}
                    onClick={() => update("attending", false)}
                    testid="rsvp-attending-no"
                  >
                    {t.rsvp.no}
                  </ChoiceBtn>
                </div>
              </Field>

              {form.attending && (
                <>
                  <Field label={t.rsvp.companions}>
                    <input
                      data-testid="rsvp-attendees-input"
                      type="number"
                      min="1"
                      max="20"
                      value={form.total_attendees}
                      onChange={(e) => update("total_attendees", e.target.value)}
                      placeholder={t.rsvp.companionsPh}
                      className="w-full bg-transparent border-b border-olive/25 px-1 py-3 font-sans text-base focus:outline-none focus:border-wine transition-colors"
                    />
                  </Field>

                  <Field label={t.rsvp.menuChoice}>
                    <div className="space-y-3 pt-2">
                      <MenuCounter
                        label={t.rsvp.menuLubina}
                        value={form.menus.lubina}
                        canIncrement={assigned < totalAttendees}
                        onInc={() => setMenu("lubina", +1)}
                        onDec={() => setMenu("lubina", -1)}
                        testid="rsvp-count-lubina"
                      />
                      <MenuCounter
                        label={t.rsvp.menuIberico}
                        value={form.menus.iberico}
                        canIncrement={assigned < totalAttendees}
                        onInc={() => setMenu("iberico", +1)}
                        onDec={() => setMenu("iberico", -1)}
                        testid="rsvp-count-iberico"
                      />
                      <MenuCounter
                        label={t.rsvp.menuTeen1417}
                        value={form.menus.menu_14_17}
                        canIncrement={assigned < totalAttendees}
                        onInc={() => setMenu("menu_14_17", +1)}
                        onDec={() => setMenu("menu_14_17", -1)}
                        testid="rsvp-count-menu1417"
                      />
                      <MenuCounter
                        label={t.rsvp.menuChild613}
                        value={form.menus.menu_6_13}
                        canIncrement={assigned < totalAttendees}
                        onInc={() => setMenu("menu_6_13", +1)}
                        onDec={() => setMenu("menu_6_13", -1)}
                        testid="rsvp-count-menu613"
                      />
                      <MenuCounter
                        label={t.rsvp.menuChild25}
                        value={form.menus.menu_2_5}
                        canIncrement={assigned < totalAttendees}
                        onInc={() => setMenu("menu_2_5", +1)}
                        onDec={() => setMenu("menu_2_5", -1)}
                        testid="rsvp-count-menu25"
                      />
                    </div>

                    <div
                      data-testid="menu-balance"
                      className={`mt-4 flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase ${
                        over ? "text-wine" : remaining === 0 ? "text-olive" : "text-olive/80"
                      }`}
                    >
                      {over ? (
                        <>
                          <AlertCircle className="w-4 h-4" strokeWidth={1.6} />
                          <span>{t.rsvp.overAssigned}</span>
                        </>
                      ) : remaining > 0 ? (
                        <>
                          <AlertCircle className="w-4 h-4" strokeWidth={1.6} />
                          <span>
                            {t.rsvp.remaining}: {remaining} / {totalAttendees}
                          </span>
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" strokeWidth={2} />
                          <span>
                            {assigned} / {totalAttendees}
                          </span>
                        </>
                      )}
                    </div>
                  </Field>

                  <Field label={t.rsvp.allergies}>
                    <input
                      data-testid="rsvp-allergies-input"
                      value={form.allergies}
                      onChange={(e) => update("allergies", e.target.value)}
                      placeholder={t.rsvp.allergiesPh}
                      className="w-full bg-transparent border-b border-olive/25 px-1 py-3 font-sans text-base focus:outline-none focus:border-wine transition-colors placeholder:text-olive/50 placeholder:font-light"
                    />
                  </Field>
                </>
              )}

              <Field label={t.rsvp.message}>
                <textarea
                  data-testid="rsvp-message-input"
                  rows="3"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder={t.rsvp.messagePh}
                  className="w-full bg-transparent border-b border-olive/25 px-1 py-3 font-sans text-base focus:outline-none focus:border-wine transition-colors placeholder:text-olive/50 placeholder:font-light resize-none"
                />
              </Field>

              {form.attending && incomplete && (
                <p data-testid="rsvp-validation-msg" className="text-xs text-wine font-editorial italic text-center">
                  {over ? t.rsvp.overAssigned : t.rsvp.assignAll}
                </p>
              )}

              <button
                type="submit"
                disabled={sending || (form.attending && incomplete)}
                data-testid="rsvp-submit-btn"
                className="w-full mt-2 bg-wine text-cream py-4 rounded-full text-[11px] tracking-[0.3em] uppercase transition-all duration-500 hover:bg-wine-deep hover:scale-[1.01] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {sending ? t.rsvp.sending : t.rsvp.submit}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-olive mb-1">{label}</label>
    {children}
  </div>
);

const ChoiceBtn = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 px-5 py-3.5 rounded-full text-[11px] tracking-[0.2em] uppercase transition-all duration-300 border ${
      active
        ? "bg-wine text-cream border-wine shadow-md"
        : "bg-transparent text-olive border-olive/25 hover:border-wine hover:text-wine"
    }`}
  >
    <span className="inline-flex items-center justify-center gap-2">
      {active && <Check className="w-3.5 h-3.5" strokeWidth={2} />}
      {children}
    </span>
  </button>
);

const MenuCounter = ({ label, value, canIncrement, onInc, onDec, testid }) => (
  <div
    className="flex items-center justify-between gap-4 py-3 px-4 rounded-xl bg-cream/50 border border-olive/15"
    data-testid={testid}
  >
    <span className="font-editorial text-base md:text-lg text-charcoal flex-1 leading-tight">
      {label}
    </span>
    <div className="flex items-center gap-3 shrink-0">
      <button
        type="button"
        onClick={onDec}
        disabled={value <= 0}
        aria-label="decrement"
        data-testid={`${testid}-dec`}
        className="w-9 h-9 rounded-full border border-olive/30 text-olive flex items-center justify-center transition-all duration-300 hover:border-wine hover:text-wine hover:bg-wine/5 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-olive/30 disabled:hover:text-olive"
      >
        <Minus className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
      <span
        data-testid={`${testid}-value`}
        className="font-serif text-xl text-wine tabular-nums w-6 text-center"
      >
        {value}
      </span>
      <button
        type="button"
        onClick={onInc}
        disabled={!canIncrement}
        aria-label="increment"
        data-testid={`${testid}-inc`}
        className="w-9 h-9 rounded-full border border-wine/40 text-wine flex items-center justify-center transition-all duration-300 hover:bg-wine hover:text-cream hover:border-wine disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-wine"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  </div>
);

export default RSVPForm;
